/**
 * @fileoverview Dammen Leaderboard Component
 * @description Displays win/loss rankings for Dammen
 */

import React, { useState, useEffect, useCallback, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { DammenLeaderboardEntry } from "../types";

interface DammenLeaderboardProps {
  mode: "free" | "weekend";
  weekendId?: string;
  maxEntries?: number;
  onUserPress?: (userId: string) => void;
}

/**
 * Dammen Leaderboard Component
 */
export const DammenLeaderboard: React.FC<DammenLeaderboardProps> = memo(
  ({ mode, weekendId, maxEntries = 10, onUserPress }) => {
    const colors = useThemeColors();
    const { activeProfile } = useAuth();
    const [entries, setEntries] = useState<DammenLeaderboardEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [myRank, setMyRank] = useState<number | null>(null);

    /**
     * Fetch leaderboard data
     */
    const fetchLeaderboard = useCallback(async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("user_game_stats")
          .select(`
            user_id,
            games_played,
            games_won,
            games_lost,
            games_drawn,
            stats,
            profiles:user_id (
              full_name,
              nickname,
              avatar_url
            )
          `)
          .eq("game_slug", "dammen")
          .eq("mode", mode)
          .gt("games_played", 0)
          .order("games_won", { ascending: false })
          .limit(maxEntries);

        if (error) throw error;

        const leaderboard: DammenLeaderboardEntry[] = (data || []).map(
          (entry: any, index: number) => {
            const played = entry.games_played || 0;
            const won = entry.games_won || 0;
            const winRate = played > 0 ? Math.round((won / played) * 100) : 0;

            return {
              rank: index + 1,
              userId: entry.user_id,
              userName:
                entry.profiles?.nickname || entry.profiles?.full_name || "Onbekend",
              avatarUrl: entry.profiles?.avatar_url,
              gamesPlayed: played,
              gamesWon: won,
              winRate,
            };
          }
        );

        setEntries(leaderboard);

        // Find current user's rank
        if (activeProfile?.id) {
          const userEntry = leaderboard.find((e) => e.userId === activeProfile.id);
          setMyRank(userEntry?.rank || null);
        }
      } catch (error) {
        console.error("Error fetching Dammen leaderboard:", error);
      } finally {
        setLoading(false);
      }
    }, [mode, maxEntries, activeProfile?.id]);

    useEffect(() => {
      fetchLeaderboard();
    }, [fetchLeaderboard]);

    /**
     * Get initials from name
     */
    const getInitials = (name: string): string => {
      return name
        .split(" ")
        .map((n) => n.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    /**
     * Get rank medal color
     */
    const getRankColor = (rank: number): string => {
      switch (rank) {
        case 1:
          return "#FFD700"; // Gold
        case 2:
          return "#C0C0C0"; // Silver
        case 3:
          return "#CD7F32"; // Bronze
        default:
          return colors.textSecondary;
      }
    };

    if (loading) {
      return (
        <View style={[styles.container, { backgroundColor: colors.surface }]}>
          <ActivityIndicator color={colors.primary} />
        </View>
      );
    }

    if (entries.length === 0) {
      return (
        <View style={[styles.container, { backgroundColor: colors.surface }]}>
          <Ionicons name="trophy-outline" size={32} color={colors.textTertiary} />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            Nog geen spellen gespeeld
          </Text>
          <Text style={[styles.emptySubtext, { color: colors.textTertiary }]}>
            Speel een potje om op de ranglijst te komen!
          </Text>
        </View>
      );
    }

    return (
      <View style={[styles.container, { backgroundColor: colors.surface }]}>
        <View style={styles.header}>
          <Ionicons name="trophy" size={20} color={colors.warning} />
          <Text style={[styles.title, { color: colors.text }]}>
            {mode === "weekend" ? "Weekend Ranglijst" : "Ranglijst"} 🏆
          </Text>
        </View>

        {entries.map((entry) => {
          const isMe = entry.userId === activeProfile?.id;

          return (
            <TouchableOpacity
              key={entry.userId}
              style={[
                styles.entryRow,
                isMe && { backgroundColor: colors.primaryLight },
              ]}
              onPress={() => onUserPress?.(entry.userId)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`${entry.userName}, rank ${entry.rank}`}
            >
              {/* Rank */}
              <View style={styles.rankContainer}>
                {entry.rank <= 3 ? (
                  <Ionicons
                    name="medal"
                    size={24}
                    color={getRankColor(entry.rank)}
                  />
                ) : (
                  <Text style={[styles.rankText, { color: colors.textSecondary }]}>
                    {entry.rank}
                  </Text>
                )}
              </View>

              {/* Avatar */}
              <View
                style={[styles.avatar, { backgroundColor: colors.border }]}
              >
                {entry.avatarUrl ? (
                  <Image
                    source={{ uri: entry.avatarUrl }}
                    style={styles.avatarImage}
                  />
                ) : (
                  <Text style={[styles.avatarText, { color: colors.text }]}>
                    {getInitials(entry.userName)}
                  </Text>
                )}
              </View>

              {/* Name and stats */}
              <View style={styles.infoContainer}>
                <Text
                  style={[
                    styles.userName,
                    { color: colors.text },
                    isMe && styles.myName,
                  ]}
                  numberOfLines={1}
                >
                  {entry.userName} {isMe && "(jij)"}
                </Text>
                <Text style={[styles.statsText, { color: colors.textSecondary }]}>
                  {entry.gamesWon} gewonnen • {entry.winRate}% winrate
                </Text>
              </View>

              {/* Games won badge */}
              <View
                style={[styles.winBadge, { backgroundColor: colors.success }]}
              >
                <Text style={styles.winBadgeText}>{entry.gamesWon}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }
);

DammenLeaderboard.displayName = "DammenLeaderboard";

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    gap: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
  },
  entryRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  rankContainer: {
    width: 32,
    alignItems: "center",
  },
  rankText: {
    fontSize: 16,
    fontWeight: "600",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    overflow: "hidden",
  },
  avatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarText: {
    fontSize: 14,
    fontWeight: "600",
  },
  infoContainer: {
    flex: 1,
    marginLeft: 12,
  },
  userName: {
    fontSize: 14,
    fontWeight: "600",
  },
  myName: {
    fontWeight: "700",
  },
  statsText: {
    fontSize: 12,
    marginTop: 2,
  },
  winBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    minWidth: 40,
    alignItems: "center",
  },
  winBadgeText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 14,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 12,
    textAlign: "center",
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 4,
    textAlign: "center",
  },
});

